import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { asyncStatus } from "../../../utils/asyncStatus";
import { getVendorStatusAsync } from "../../../services/authentication";
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { themeOrange, themeBlue } from "../../../utils/colorTheme";
import ButtonComp from "../../../component/common/ButtonComp";
import { useParams } from "react-router-dom";
import { IoCard, IoBusinessOutline, IoCash, IoSwapHorizontal, IoCheckmarkCircle, IoFingerPrint } from "react-icons/io5";

const VendorAccountTab = () => {
  const { id } = useParams();
  const dispatch = useDispatch();

  const {
    get_vendor_status_status,
    get_vendor_status_data,
  } = useSelector((state) => state.userAuth);

  const {
    get_user_by_id_data,
  } = useSelector((state) => state.dashboard_users);

  const isCheckStripeStatusLoading = get_vendor_status_status === asyncStatus.LOADING;

  const stripeDetails =
    get_user_by_id_data?.stripe_details &&
    JSON.parse(get_user_by_id_data?.stripe_details);

  const _handleOpenClick = () => {
    dispatch(getVendorStatusAsync({ user_id: id }));
  };

  const stripeDetailArr = [
    { label: "Type", value: stripeDetails?.type || "-", icon: <IoCard size={18} /> },
    { label: "Business Type", value: stripeDetails?.business_type || "-", icon: <IoBusinessOutline size={18} /> },
    { label: "Currency", value: (stripeDetails?.default_currency || "-").toUpperCase(), icon: <IoCash size={18} /> },
    { label: "Transfers", value: stripeDetails?.capabilities?.transfers || "-", icon: <IoSwapHorizontal size={18} /> },
    { label: "Account Status", value: get_user_by_id_data?.stripe_account_status || "-", icon: <IoCheckmarkCircle size={18} /> },
    { label: "Stripe Account ID", value: get_user_by_id_data?.stripe_id || "-", icon: <IoFingerPrint size={18} /> },
  ];

  const isBusinessProfileMissing =
    get_vendor_status_data?.account_details?.requirements?.past_due?.some(
      (req) => req.startsWith("business_profile")
    );
  const isIndividualMissing =
    get_vendor_status_data?.account_details?.requirements?.past_due?.some(
      (req) => req.startsWith("individual")
    );

  if (!get_user_by_id_data?.account_link) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: "40vh" }}>
        <Typography sx={{ color: "#bbb", fontSize: "14px" }}>
          No Stripe account connected
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack sx={{ pb: 4, mt: 2 }}>
      {/* Account Details */}
      <Stack
        sx={{
          bgcolor: "#fff",
          borderRadius: "14px",
          boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, pt: 2.5, pb: 2 }}
        >
          <Typography sx={{ fontSize: "16px", fontWeight: 700, color: themeBlue }}>
            Stripe Account
          </Typography>
          {get_user_by_id_data?.stripe_id && (
            <ButtonComp
              disabled={isCheckStripeStatusLoading}
              onClick={_handleOpenClick}
              label={
                isCheckStripeStatusLoading ? (
                  <CircularProgress size={16} sx={{ color: "white" }} />
                ) : (
                  "Check Status"
                )
              }
              style={{
                width: "auto",
                height: "auto",
                padding: "6px 18px",
                borderRadius: "10px",
                fontSize: "13px",
                background: "linear-gradient(135deg, #F15A24 0%, #C53F10 100%)",
              }}
            />
          )}
        </Stack>

        <Grid container>
          {stripeDetailArr.map(({ label, value, icon }, i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Stack
                direction="row"
                alignItems="center"
                gap={2}
                sx={{
                  px: 3,
                  py: 2,
                  borderTop: "1px solid #f5f5f5",
                }}
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "10px",
                    bgcolor: "#F8F9FA",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#888",
                    flexShrink: 0,
                  }}
                >
                  {icon}
                </Box>
                <Stack>
                  <Typography sx={{ fontSize: "11px", color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontSize: "14px", color: themeBlue, fontWeight: 500, wordBreak: "break-all" }}>
                    {value}
                  </Typography>
                </Stack>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Stack>

      {/* Rejected Warning */}
      {get_vendor_status_data?.user?.stripe_account_status === "Rejected" && (
        <Stack
          sx={{
            mt: 2.5,
            p: 2.5,
            borderRadius: "14px",
            bgcolor: "#FFF3E0",
            border: "1px solid #FFE0B2",
          }}
          gap={1}
        >
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#E65100" }}>
            Account not approved — pending Stripe requirements
          </Typography>
          {get_vendor_status_data?.account_details?.requirements?.past_due?.length > 0 && (
            <Typography sx={{ fontSize: "13px", color: "#BF360C" }}>
              Need to update{" "}
              {isBusinessProfileMissing ? "business profile" : ""}
              {isBusinessProfileMissing && isIndividualMissing ? " and " : ""}
              {isIndividualMissing ? "individual details" : ""}.
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default VendorAccountTab;
